/**
 * Admin sign-in screen. Two-step form: email + password → TOTP code.
 * No reset password / forgot password yet (admin accounts are
 * support-provisioned; recovery is via recovery codes at the TOTP
 * step).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { storeBearer, useSignInMutation, useTotpVerifyMutation } from '../api/auth.js';

type Stage = { kind: 'credentials' } | { kind: 'totp'; pendingToken: string };

export function SignInRoute() {
  const [stage, setStage] = useState<Stage>({ kind: 'credentials' });
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-4">
      <div className="w-full max-w-md rounded-xl bg-white p-8 shadow">
        <h1 className="font-display text-2xl font-semibold text-neutral-900">funkihoo admin</h1>
        <p className="mt-1 text-sm text-neutral-600">
          {stage.kind === 'credentials'
            ? 'Sign in with your admin email + password.'
            : 'Open your authenticator and enter the 6-digit code. Recovery codes also accepted.'}
        </p>

        {stage.kind === 'credentials' ? (
          <CredentialsForm onSuccess={(pendingToken) => setStage({ kind: 'totp', pendingToken })} />
        ) : (
          <TotpForm
            pendingToken={stage.pendingToken}
            onSuccess={(token) => {
              storeBearer(token);
              navigate('/');
            }}
            onCancel={() => setStage({ kind: 'credentials' })}
          />
        )}
      </div>
    </div>
  );
}

function CredentialsForm({ onSuccess }: { onSuccess: (pendingToken: string) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const signIn = useSignInMutation();
  const [err, setErr] = useState<string | null>(null);

  return (
    <form
      className="mt-6 space-y-3"
      onSubmit={async (e) => {
        e.preventDefault();
        setErr(null);
        try {
          const res = await signIn.mutateAsync({ email, password });
          onSuccess(res.pendingToken);
        } catch (cause) {
          setErr(
            cause instanceof Error && cause.message.includes('totp_not_enrolled')
              ? 'TOTP not enrolled — contact support to set it up.'
              : 'Incorrect email or password.',
          );
        }
      }}
    >
      <label className="block">
        <span className="block text-neutral-700 text-sm">Email</span>
        <input
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
        />
      </label>
      <label className="block">
        <span className="block text-neutral-700 text-sm">Password</span>
        <input
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
        />
      </label>
      {err && <p className="text-sm text-danger-600">{err}</p>}
      <button
        type="submit"
        disabled={signIn.isPending || !email || !password}
        className="rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
      >
        {signIn.isPending ? 'Signing in…' : 'Sign in'}
      </button>
    </form>
  );
}

function TotpForm({
  pendingToken,
  onSuccess,
  onCancel,
}: {
  pendingToken: string;
  onSuccess: (token: string) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState('');
  const verify = useTotpVerifyMutation();
  const [err, setErr] = useState<string | null>(null);

  return (
    <form
      className="mt-6 space-y-3"
      onSubmit={async (e) => {
        e.preventDefault();
        setErr(null);
        try {
          const res = await verify.mutateAsync({ pendingToken, code });
          onSuccess(res.token);
        } catch {
          setErr('Invalid code. Try the next one, or paste a recovery code.');
        }
      }}
    >
      <label className="block">
        <span className="block text-neutral-700 text-sm">Code</span>
        <input
          type="text"
          inputMode="text"
          autoComplete="one-time-code"
          required
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="123456 or ABCD-EFGH-IJKL-MNOP"
          className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 font-mono text-sm"
        />
      </label>
      {err && <p className="text-sm text-danger-600">{err}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={verify.isPending || code.length === 0}
          className="rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {verify.isPending ? 'Verifying…' : 'Continue'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-neutral-300 px-3 py-2 text-sm text-neutral-700"
        >
          Back
        </button>
      </div>
    </form>
  );
}
